import React, { useEffect, useState } from "react";
import { useIds } from "../../../hook/AdminContext";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import {
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
} from "@mui/material";

import LocationOnIcon from "@mui/icons-material/LocationOn";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import WorkIcon from "@mui/icons-material/Work";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RepeatIcon from "@mui/icons-material/Repeat";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  add_job_alert,
  job_alert_details,
  update_job_alert,
} from "../../../DAL/jobs/jobs";
import { enqueueSnackbar } from "notistack";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { convertTimeToServerTimeZone } from "../../../utils/Constant";
import CircularLoader from "../../../components/loader/CircularLoader";

export default function AddOrUpdateAlert() {
  const navigate = useNavigate();
  const location = useLocation();
  const { source_slug, jobtype_slug, alert_id } = useParams();
  const { setBreadCrumb, adminSetting } = useIds();

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [inputs, setInputs] = useState({
    location: "",
    job_title: "",
    frequency_salary: "",
    sent_time: dayjs().hour(9).minute(0).second(0),
    second_sent_time: dayjs().hour(16).minute(0).second(0),
    frequency: "",
    status: true,
  });

  const [formType, setFormType] = useState(alert_id ? "edit" : "add");
  const [isLoading, setIsLoading] = useState(alert_id ? true : false);

  const FREQUENCY_OPTIONS = [
    { value: "twice_a_day", label: "Twice Daily" },
    { value: "every_day", label: "Daily" },
    { value: "every_two_days", label: "Every 2 days" },
    { value: "every_three_days", label: "Every 3 days" },
    { value: "weekly", label: "Weekly" },
    // { value: "bi_weekly", label: "Bi Weekly" },
  ];

  // -----------------------------
  // HANDLE INPUT CHANGES
  // -----------------------------
  const handleChangeInput = (e) => {
    const { name, value } = e.target;

    if (name === "frequency" && value !== "Twice Daily") {
      setInputs((prev) => ({
        ...prev,
        frequency: value,
        second_sent_time: dayjs().hour(16).minute(0).second(0),
      }));
      return;
    }

    setInputs((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // -----------------------------
  // PREFILL DATA IN EDIT MODE
  // -----------------------------

  const handleSetEditData = (data) => {
    setInputs((prev) => ({
      ...prev,
      location: data.location,
      job_title: data.job_title || "",
      frequency_salary: data.frequency_salary || "",
      sent_time: data.sent_time
        ? dayjs(
            convertTimeToServerTimeZone(
              data.sent_time,
              adminSetting?.time_zone,
              userTimeZone
            ),
            "HH:mm"
          )
        : dayjs().hour(9).minute(0).second(0),
      second_sent_time: data.second_sent_time
        ? dayjs(
            convertTimeToServerTimeZone(
              data.second_sent_time,
              adminSetting?.time_zone,
              userTimeZone
            ),
            "HH:mm"
          )
        : dayjs().hour(16).minute(0).second(0),
      frequency: data.frequency || "",
      status: data.status || true,
    }));
    setIsLoading(false);
  };
  useEffect(() => {
    if (formType !== "edit") return;

    // 1️⃣ Check if data came from state (list → edit)
    const stateAlert = location?.state?.alert;
    if (stateAlert) {
      handleSetEditData(stateAlert);
      return;
    }

    // 2️⃣ Otherwise fetch from API
    const loadData = async () => {
      const resp = await job_alert_details(alert_id); // YOUR API FUNCTION
      if (resp?.code === 200) {
        handleSetEditData(resp.data.alert);
      } else {
        enqueueSnackbar(resp.message, { variant: "error" });
        setIsLoading(false);
      }
    };

    loadData();
  }, [formType, alert_id]);

  // -----------------------------
  // BREADCRUMB
  // -----------------------------
  useEffect(() => {
    setBreadCrumb([
      {
        name: "Live Job Feed",
        path: `/${source_slug}/${jobtype_slug}`,
        active: false,
      },
      {
        name: "Job Alerts",
        path: `/${source_slug}/${jobtype_slug}/job-alerts`,
        active: false,
      },
      {
        name: formType === "edit" ? "Update Alert" : "Add Alert",
        path: `/${source_slug}/${jobtype_slug}/job-alerts/${
          formType == "edit" ? `edit-alert/${alert_id}` : "add-alert"
        }`,
        active: false,
      },
    ]);
  }, []);

  // -----------------------------
  // SUBMIT
  // -----------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    const postData = {
      location: inputs.location,
      job_title: inputs.job_title,
      // frequency_salary: inputs.frequency_salary,
      frequency_salary: "",
      sent_time: dayjs(inputs.sent_time).format("HH:mm"),
      frequency: inputs.frequency,
      status: inputs.status,
      user_timezone: userTimeZone,
    };

    if (inputs.frequency == "twice_a_day") {
      const firstTime = dayjs(inputs.sent_time, "HH:mm");
      const secondTime = dayjs(inputs.second_sent_time, "HH:mm");

      if (!secondTime.isAfter(firstTime)) {
        enqueueSnackbar(
          "Second email time must be after the first email time!",
          { variant: "error" }
        );
        return;
      }
      postData.second_sent_time = dayjs(inputs.second_sent_time).format(
        "HH:mm"
      );
    }
    const resp =
      formType === "add"
        ? await add_job_alert(postData)
        : await update_job_alert(alert_id, postData);
    if (resp?.code === 200) {
      enqueueSnackbar(resp.message, { variant: "success" });
      navigate(`/${source_slug}/${jobtype_slug}/job-alerts`);
    } else {
      enqueueSnackbar(resp.message, { variant: "error" });
    }
  };

  return (
    <>
      <div className="container-fluid mt-3">
        <form onSubmit={handleSubmit}>
          <div className="row mt-0">
            <div className="col-12 heading-main d-flex align-items-center justify-content-between">
              <h2>{formType === "edit" ? "Update Alert" : "Add Alert"}</h2>
            </div>
            {isLoading ? (
              <CircularLoader />
            ) : (
              <>
                {/* Job Title */}
                <div className="col-12 col-md-6">
                  <TextField
                    fullWidth
                    margin="normal"
                    required
                    name="job_title"
                    label="Job Title"
                    value={inputs.job_title}
                    onChange={handleChangeInput}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <WorkIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>

                {/* Location */}
                <div className="col-12 col-md-6">
                  <TextField
                    fullWidth
                    margin="normal"
                    name="location"
                    required
                    label="Location"
                    value={inputs.location}
                    onChange={handleChangeInput}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>

                {/* Salary */}
                {/* <div className="col-12 col-md-6">
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="frequency-label">
                      Frequency Salary
                    </InputLabel>
                    <Select
                      labelId="frequency-label"
                      name="frequency_salary"
                      value={inputs.frequency_salary}
                      onChange={(e) => handleChangeInput(e)}
                      input={
                        <OutlinedInput
                          startAdornment={
                            <InputAdornment position="start">
                              <AttachMoneyIcon />{" "}
                            </InputAdornment>
                          }
                          label="Frequency Salary"
                        />
                      }
                    >
                      <MenuItem value="">No Frequency</MenuItem>
                      <MenuItem value="Hourly">Hourly</MenuItem>
                      <MenuItem value="Yearly">Yearly</MenuItem>
                    </Select>
                  </FormControl>
                </div> */}

                {/* Frequency */}
                <div className="col-12 col-md-6">
                  <TextField
                    select
                    fullWidth
                    margin="normal"
                    required
                    name="frequency"
                    label="Alert Frequency"
                    value={inputs.frequency}
                    onChange={handleChangeInput}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <RepeatIcon />
                        </InputAdornment>
                      ),
                    }}
                  >
                    {FREQUENCY_OPTIONS.map((freq) => (
                      <MenuItem key={freq.value} value={freq.value}>
                        {freq.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>

                {/* Email Time */}
                <div className="col-12 col-md-6">
                  <LocalizationProvider
                    className="hide-scrollbar"
                    dateAdapter={AdapterDayjs}
                  >
                    <TimePicker
                      label={
                        inputs.frequency === "Twice Daily"
                          ? "First Email Time"
                          : "Email Time"
                      }
                      value={inputs.sent_time}
                      onChange={(newValue) =>
                        handleChangeInput({
                          target: { name: "sent_time", value: newValue },
                        })
                      }
                      ampm={false} // 🔥 THIS MAKES IT 24-HOUR
                      slotProps={{
                        textField: {
                          required: true,
                          fullWidth: true,
                          sx: {
                            "& .MuiInputAdornment-root": {
                              order: -1, // 🔥 move to the left
                              marginRight: "12px",
                              marginLeft: "8px",
                            },
                            "& .MuiInputBase-input": {
                              paddingLeft: "0px", // adjust text alignment
                            },
                          },
                        },
                      }}
                      className="mt-3"
                    />
                  </LocalizationProvider>
                </div>

                {/* Second Time (only for twice daily) */}
                {inputs.frequency === "twice_a_day" && (
                  <div className="col-12 col-md-6">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <TimePicker
                        label="Second Email Time"
                        value={inputs.second_sent_time}
                        onChange={(newValue) =>
                          handleChangeInput({
                            target: {
                              name: "second_sent_time",
                              value: newValue,
                            },
                          })
                        }
                        ampm={false}
                        slotProps={{
                          textField: {
                            required: true,
                            fullWidth: true,
                            sx: {
                              "& .MuiInputAdornment-root": {
                                order: -1, // 🔥 move to the left
                                marginRight: "12px",
                                marginLeft: "8px",
                              },
                              "& .MuiInputBase-input": {
                                paddingLeft: "0px", // adjust text alignment
                              },
                            },
                          },
                        }}
                        className="mt-3"
                      />
                    </LocalizationProvider>
                  </div>
                )}

                {/* Status */}
                {formType == "edit" && (
                  <div className="col-12 col-md-6">
                    <TextField
                      select
                      fullWidth
                      margin="normal"
                      name="status"
                      label="Status"
                      value={inputs.status}
                      onChange={handleChangeInput}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CheckCircleIcon />
                          </InputAdornment>
                        ),
                      }}
                    >
                      <MenuItem value={true}>Active</MenuItem>
                      <MenuItem value={false}>Inactive</MenuItem>
                    </TextField>
                  </div>
                )}

                {/* BUTTON */}
                <div className="col-12 mt-4 text-end">
                  <button className="btn-contained" type="submit">
                    {formType === "edit" ? "Update Alert" : "Add Alert"}
                  </button>
                </div>
              </>
            )}
          </div>
        </form>
      </div>
    </>
  );
}
